import { type FormEvent, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { Alert, Button, Field, Spinner } from "@/components/ui";
import { ApiError, api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { AuthLayout } from "./auth/AuthLayout";
import BoardPage, { type ShareInfo } from "./board/BoardPage";

interface Info extends ShareInfo {
  needsPassword: boolean;
  unlocked: boolean;
  welcomeMessage: string | null;
}

/** Public entry for published boards: unlock (password), welcome message, then the read-only board. */
export default function SharePage() {
  const { token = "" } = useParams();
  const [params] = useSearchParams();
  const t = useT();
  const [info, setInfo] = useState<Info | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [welcomed, setWelcomed] = useState(false);

  useEffect(() => {
    api<Info>(`/api/share/${token}`)
      .then(setInfo)
      .catch(() => setError(t.shareNotFound));
  }, [token, t]);

  async function unlock(e?: FormEvent) {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api(`/api/share/${token}/unlock`, { method: "POST", json: { password: password || undefined } });
      setInfo((i) => (i ? { ...i, unlocked: true, needsPassword: false } : i));
    } catch (err) {
      setError(err instanceof ApiError && err.code === "wrong_password" ? t.wrongPassword : t.error);
    } finally {
      setBusy(false);
    }
  }
  // Links without a password still need the grant cookie: unlock silently.
  useEffect(() => {
    if (info && !info.unlocked && !info.needsPassword) void unlock();
    // biome-ignore lint/correctness/useExhaustiveDependencies: run once per info load
  }, [info?.unlocked, info?.needsPassword]);

  if (error && !info)
    return (
      <AuthLayout title={t.sharedBoard}>
        <Alert kind="error">{error}</Alert>
        <Link to="/login" className="mt-4 inline-block text-sm text-muted hover:text-text">
          {t.signIn}
        </Link>
      </AuthLayout>
    );
  if (!info)
    return (
      <AuthLayout title={t.sharedBoard}>
        <Spinner />
      </AuthLayout>
    );
  if (info.needsPassword)
    return (
      <AuthLayout title={info.title}>
        <p className="mb-4 text-sm text-muted">{t.protectedBoard}</p>
        <form onSubmit={unlock} className="space-y-4">
          <Field
            label={t.password}
            name="password"
            type="password"
            autoFocus
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {error ? <Alert kind="error">{error}</Alert> : null}
          <Button type="submit" className="w-full" loading={busy}>
            {t.open}
          </Button>
        </form>
      </AuthLayout>
    );
  if (!info.unlocked)
    return (
      <AuthLayout title={info.title}>
        <Spinner />
      </AuthLayout>
    );
  if (info.welcomeMessage && !welcomed)
    return (
      <AuthLayout title={info.title}>
        <p className="mb-6 whitespace-pre-wrap text-muted">{info.welcomeMessage}</p>
        <Button className="w-full" onClick={() => setWelcomed(true)}>
          {t.open}
        </Button>
      </AuthLayout>
    );
  const boardId = params.get("b") ?? info.boardId;
  return <BoardPage key={boardId} share={{ ...info, boardId }} />;
}
